import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';

type LocaleContent = {
  before: string[];
  during: string[];
  after: string[];
};

const emptyLocale = (): LocaleContent => ({
  before: [''],
  during: [''],
  after: [''],
});

function normalizeLists(c: LocaleContent): LocaleContent {
  return {
    before: c.before.map((s) => s.trim()),
    during: c.during.map((s) => s.trim()),
    after: c.after.map((s) => s.trim()),
  };
}

function validateForSave(en: LocaleContent, ar: LocaleContent): string | null {
  for (const [label, loc] of [
    ['EN', en],
    ['AR', ar],
  ] as const) {
    for (const key of ['before', 'during', 'after'] as const) {
      const items = loc[key].filter((s) => s.length > 0);
      if (items.length < 1) {
        return `${label}: ${key} needs at least one item`;
      }
    }
  }
  return null;
}

function ListSection({
  title,
  items,
  onChange,
}: {
  title: string;
  items: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      <div className="space-y-2">
        {items.map((line, i) => (
          <div key={i} className="flex gap-2 items-start">
            <input
              className="flex-1 border rounded px-3 py-2 text-sm"
              value={line}
              onChange={(e) => {
                const next = [...items];
                next[i] = e.target.value;
                onChange(next);
              }}
              placeholder="Tip…"
            />
            <button
              type="button"
              className="text-red-600 text-sm px-2 py-2 shrink-0"
              onClick={() => onChange(items.filter((_, j) => j !== i))}
              disabled={items.length <= 1}
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          className="text-sm text-orange-600 font-medium"
          onClick={() => onChange([...items, ''])}
        >
          + Add line
        </button>
      </div>
    </div>
  );
}

export default function HygieneSafetySettings() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [langTab, setLangTab] = useState<'en' | 'ar'>('en');
  const [isActive, setIsActive] = useState(true);
  const [en, setEn] = useState<LocaleContent>(() => emptyLocale());
  const [ar, setAr] = useState<LocaleContent>(() => emptyLocale());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/hygiene-tips');
      const data = res.data?.data;
      if (!data?.content) {
        setError('Invalid response');
        return;
      }
      setIsActive(data.isActive !== false);
      const c = data.content as { en: LocaleContent; ar: LocaleContent };
      const pad = (loc: LocaleContent): LocaleContent => ({
        before: loc.before?.length ? [...loc.before] : [''],
        during: loc.during?.length ? [...loc.during] : [''],
        after: loc.after?.length ? [...loc.after] : [''],
      });
      setEn(pad(c.en));
      setAr(pad(c.ar));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    const nEn = normalizeLists(en);
    const nAr = normalizeLists(ar);
    const v = validateForSave(nEn, nAr);
    if (v) {
      setError(v);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.put('/admin/hygiene-tips', {
        isActive,
        content: { en: nEn, ar: nAr },
      });
      await load();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setError(typeof msg === 'string' ? msg : Array.isArray(msg) ? msg.join(', ') : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const loc = langTab === 'en' ? en : ar;
  const setLoc = langTab === 'en' ? setEn : setAr;

  if (loading) {
    return <p className="text-gray-500">{t('common.loading')}</p>;
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('hygiene.title')}</h1>
        <p className="text-gray-600 text-sm mt-1">{t('hygiene.subtitle')}</p>
      </div>

      <div className="flex items-center justify-between bg-white border rounded-lg p-4 shadow-sm">
        <div>
          <div className="font-medium text-gray-900">{t('hygiene.enableSection')}</div>
          <div className="text-sm text-gray-500">{t('hygiene.enableHint')}</div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={isActive}
          onClick={() => setIsActive(!isActive)}
          className={`relative w-12 h-7 rounded-full transition-colors ${isActive ? 'bg-orange-500' : 'bg-gray-300'}`}
        >
          <span
            className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
              isActive ? 'left-6' : 'left-1'
            }`}
          />
        </button>
      </div>

      <div className="border-b flex gap-2">
        <button
          type="button"
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${
            langTab === 'en' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500'
          }`}
          onClick={() => setLangTab('en')}
        >
          English
        </button>
        <button
          type="button"
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${
            langTab === 'ar' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500'
          }`}
          onClick={() => setLangTab('ar')}
        >
          العربية
        </button>
      </div>

      <div className="bg-white border rounded-lg p-6 shadow-sm space-y-8">
        <ListSection
          title={t('hygiene.beforeCooking')}
          items={loc.before}
          onChange={(next) => setLoc({ ...loc, before: next })}
        />
        <ListSection
          title={t('hygiene.duringCooking')}
          items={loc.during}
          onChange={(next) => setLoc({ ...loc, during: next })}
        />
        <ListSection
          title={t('hygiene.afterCooking')}
          items={loc.after}
          onChange={(next) => setLoc({ ...loc, after: next })}
        />
      </div>

      {error && <div className="text-red-600 text-sm">{error}</div>}

      <button
        type="button"
        onClick={save}
        disabled={saving}
        className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3 rounded-lg disabled:opacity-50"
      >
        {saving ? t('common.loading') : t('common.save')}
      </button>
    </div>
  );
}
